import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useCoordination } from '@atlassiansox/engagekit-ts';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { ModalTransition } from '@atlaskit/modal-dialog';

import { RecommendStandardOverPremiumModal } from '@atlassian/recommend-standard-over-premium';
import { type AnalyticsAttributes } from '@atlassian/analytics-bridge';

import { GROWTH_LUNA_EXPERIMENT } from '@confluence/onboarding-helpers/entry-points/constants/growth-constants';
import { useOnboardingState } from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { Attribution, withTransparentErrorBoundary } from '@confluence/error-boundary';
import { ScreenEvent } from '@confluence/analytics';
import {
	RECOMMEND_BUY_STANDARD_PREMIUM_TRIAL,
	ExperienceStart,
	ExperienceSuccess,
	ExperienceFailure,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker';
import { useCoordinationClient } from '@confluence/engagement-provider';

import { usePremiumDirectBuyStandardExperiment } from '../hooks/usePremiumDirectBuyStandardExperiment';
import { useFireExposureEvents } from '../hooks/useFireExposureEvents';
import { usePricing } from '../hooks/usePricing';
import { COHORTS } from '../utils/experimentConfig';

interface RecommendStandardOverPremiumModalComponentProps {
	isEligibleTreatment: boolean;
	analyticsAttributes: AnalyticsAttributes;
	cohort: string;
	changeOfferingAndPayLink: string;
	daysLeftInTrial?: number;
	endDate: string;
}

function RecommendStandardOverPremiumModalComponent({
	isEligibleTreatment,
	analyticsAttributes,
	cohort,
	changeOfferingAndPayLink,
	daysLeftInTrial,
	endDate,
}: RecommendStandardOverPremiumModalComponentProps) {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isOpen, setOpen] = useState(true);
	const [canFireExposureEvents, setCanFireExposureEvents] = useState(true);
	const experienceTracker = useContext(ExperienceTrackerContext);

	const coordinationClient = useCoordinationClient();
	const [startModalCoordination, stopModalCoordination] = useCoordination(
		coordinationClient,
		`confluence_eot_premium_buy_standard_${daysLeftInTrial}`,
	);

	const { fireExposureEvents } = useFireExposureEvents({
		skip: !startModalCoordination,
		experimentName: 'confluence_eot_premium_recommend_standard_buy',
		extraAnalytics: analyticsAttributes,
	});

	const { setOnboardingState } = useOnboardingState();

	useEffect(() => {
		if (startModalCoordination && canFireExposureEvents) {
			fireExposureEvents();
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'exposed',
					actionSubject: 'RecommendStandardOverPremiumModalExperiment',
					attributes: {
						...analyticsAttributes,
					},
					source: 'RecommendStandardOverPremiumModal',
				},
			}).fire();
			if (cohort === COHORTS.CONTROL) {
				void stopModalCoordination();
				experienceTracker.succeed({
					name: RECOMMEND_BUY_STANDARD_PREMIUM_TRIAL,
					attributes: { cohort },
				});
			}
			setCanFireExposureEvents(false);
		}
	}, [
		fireExposureEvents,
		canFireExposureEvents,
		setCanFireExposureEvents,
		cohort,
		stopModalCoordination,
		startModalCoordination,
		experienceTracker,
		analyticsAttributes,
		createAnalyticsEvent,
	]);

	const experimentTreatmentDuration = useMemo(() => {
		const daysLeft = daysLeftInTrial || 5;
		const endDate = new Date();
		endDate.setDate(endDate.getDate() + daysLeft + 1);
		return endDate;
	}, [daysLeftInTrial]);

	useEffect(() => {
		if (isEligibleTreatment && startModalCoordination) {
			setOnboardingState({
				key: GROWTH_LUNA_EXPERIMENT.RECOMMEND_STANDARD_BUY_PREMIUM_EOT_MODAL_SEEN,
				value: experimentTreatmentDuration.toISOString(),
			});
		}
	}, [
		isEligibleTreatment,
		experimentTreatmentDuration,
		setOnboardingState,
		startModalCoordination,
	]);

	const {
		standardPrice,
		premiumPrice,
		loading: pricingLoading,
		error: pricingError,
	} = usePricing(!startModalCoordination || !isEligibleTreatment);

	if (pricingLoading) {
		return null;
	}

	if (pricingError) {
		return <ExperienceFailure name={RECOMMEND_BUY_STANDARD_PREMIUM_TRIAL} error={pricingError} />;
	}

	const onCloseModal = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'RecommendStandardOverPremiumModal',
				action: 'closed',
				actionSubject: 'modal',
				attributes: { canSeeModal: startModalCoordination, ...analyticsAttributes },
			},
		}).fire();
		setOpen(false);
		void stopModalCoordination();
	};

	const onBuyStandard = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'RecommendStandardOverPremiumModal',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'buyStandard',
				attributes: analyticsAttributes,
			},
		}).fire();
		window.open(changeOfferingAndPayLink, '_blank');
	};

	return (
		<ModalTransition>
			{isOpen && isEligibleTreatment && startModalCoordination && standardPrice && premiumPrice && (
				<>
					<ScreenEvent
						name="RecommendStandardOverPremiumModal"
						id="RecommendStandardOverPremiumModal"
						attributes={analyticsAttributes}
					/>
					<RecommendStandardOverPremiumModal
						productKey="Confluence"
						trialEndDate={new Date(endDate)}
						standardPrice={standardPrice}
						premiumPrice={premiumPrice}
						onClose={onCloseModal}
						onClickBuyStandard={onBuyStandard}
						analyticsAttributes={analyticsAttributes}
					/>
					<ExperienceSuccess name={RECOMMEND_BUY_STANDARD_PREMIUM_TRIAL} attributes={{ cohort }} />
				</>
			)}
		</ModalTransition>
	);
}

export function RecommendStandardOverPremiumModalContainer() {
	const {
		loading,
		isEligible,
		isEligibleTreatment,
		trialEndDate,
		daysLeftInTrial,
		changeOfferingAndPayLink,
		cohort,
		analyticsAttributes,
	} = usePremiumDirectBuyStandardExperiment();

	if (loading || !isEligible || !trialEndDate) {
		return null;
	}

	return (
		<>
			<ExperienceStart name={RECOMMEND_BUY_STANDARD_PREMIUM_TRIAL} attributes={{ cohort }} />
			<RecommendStandardOverPremiumModalComponent
				isEligibleTreatment={isEligibleTreatment}
				endDate={trialEndDate}
				daysLeftInTrial={daysLeftInTrial}
				changeOfferingAndPayLink={changeOfferingAndPayLink}
				cohort={cohort}
				analyticsAttributes={analyticsAttributes}
			/>
		</>
	);
}

export const RecommendStandardOverPremiumModalContainerWithErrorBoundary =
	withTransparentErrorBoundary({
		attribution: Attribution.GROWTH_LUNA,
	})(RecommendStandardOverPremiumModalContainer);
